/**
 * Builds the v2-pipeline plan for `change-day-of-week` — master-only
 * permanent recurrence flip. Source-side resequence is omitted: the BE
 * renumbers on the day-of-week flip.
 */
import type { CseId } from 'types/entities/shared/ids'
import type {
  ChangeDayOfWeekIntent,
  Deps,
  OperationPlan,
  PipelineStep,
} from 'types/modules/routing'

import { makeChangeDayMasterStep } from '../steps/changeDayMaster'
import { makePreflightStep } from '../steps/preflight'
import { makeSequenceMasterStep } from '../steps/sequenceMaster'
import { makeVerifyStep } from '../steps/verify'

const STRATEGY_ID = 'v2-pipeline' as const

const collectCseIds = (intent: ChangeDayOfWeekIntent): ReadonlyArray<CseId> => {
  const cseIds: CseId[] = []
  const seen = new Set<string>()
  for (const stop of intent.movedStops) {
    const cse = stop.configuredServiceEventId
    if (cse && !seen.has(cse)) {
      seen.add(cse)
      cseIds.push(cse)
    }
  }
  return cseIds
}

export const buildChangeDayOfWeekPipeline = (
  intent: ChangeDayOfWeekIntent,
  deps: Deps,
): OperationPlan => {
  const steps: PipelineStep[] = []
  const cseIds = collectCseIds(intent)

  steps.push(makePreflightStep({ intent }, deps) as PipelineStep)

  if (cseIds.length > 0) {
    steps.push(
      makeChangeDayMasterStep(
        {
          routeId: intent.routeId,
          fromDayOfWeek: intent.fromDayOfWeek,
          toDayOfWeek: intent.toDayOfWeek,
          cseIds,
          futureStops: true,
        },
        deps,
      ) as PipelineStep,
    )
  }

  if (intent.destinationOrderedStops && intent.destinationOrderedStops.length > 0) {
    steps.push(
      makeSequenceMasterStep(
        {
          routeId: intent.routeId,
          dayOfWeek: intent.toDayOfWeek,
          orderedStops: intent.destinationOrderedStops,
        },
        deps,
      ) as PipelineStep,
    )
  }

  steps.push(makeVerifyStep({ intent }, deps) as PipelineStep)

  return {
    strategy: STRATEGY_ID,
    intent,
    steps,
  }
}
